// src/utils/errorMessages.js
// Utility functions for translating Firebase and API error codes into user-friendly messages

/**
 * Translates Firebase/backend error codes into user-friendly messages
 * @param {string} errorCode - The error code from Firebase or backend
 * @param {string} fallbackMessage - Fallback message if error code not recognized
 * @returns {string} User-friendly error message
 */
export const translateAuthError = (errorCode, fallbackMessage = 'An error occurred. Please try again.') => {
  const errorMessages = {
    // Firebase Auth Error Codes
    'INVALID_LOGIN_CREDENTIALS': 'Invalid email or password. Please check your credentials and try again.',
    'INVALID_PASSWORD': 'Invalid password. Please check your password and try again.',
    'EMAIL_NOT_FOUND': 'No account found with this email address. Please check your email or sign up for a new account.',
    'USER_DISABLED': 'This account has been disabled. Please contact support for assistance.',
    'TOO_MANY_ATTEMPTS_TRY_LATER': 'Too many unsuccessful attempts. Please wait a moment and try again.',
    'OPERATION_NOT_ALLOWED': 'This sign-in method is not enabled. Please contact support.',
    'WEAK_PASSWORD': 'Password is too weak. Please choose a stronger password with at least 6 characters.',
    'EMAIL_EXISTS': 'An account with this email already exists. Please sign in instead or use a different email.',
    'INVALID_EMAIL': 'Please enter a valid email address.',
    'MISSING_PASSWORD': 'Please enter your password.',
    'MISSING_EMAIL': 'Please enter your email address.',
    
    // Custom error codes from our backend
    'INVALID_ID_TOKEN': 'Your session has expired. Please sign in again.',
    'TOKEN_EXPIRED': 'Your session has expired. Please sign in again.',
    'REGISTRATION_FAILED': 'Failed to create account. Please try again.',
    'LOGIN_FAILED': 'Sign in failed. Please check your credentials and try again.',
    'GOOGLE_SIGNIN_FAILED': 'Google sign-in failed. Please try again.',
    'PASSWORD_RESET_FAILED': 'Failed to send password reset email. Please try again.',
    
    // Network and generic errors
    'NETWORK_ERROR': 'Network error. Please check your internet connection and try again.',
    'TIMEOUT': 'Request timed out. Please try again.',
    'INTERNAL_ERROR': 'Internal server error. Please try again later.',
    'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
    'REQUEST_FAILED': 'Request failed. Please check your internet connection and try again.',
    
    // Google Sign-in specific errors
    'auth/popup-blocked': 'Pop-up blocked by browser. Please allow pop-ups for this site and try again.',
    'auth/popup-closed-by-user': 'Sign-in cancelled. Please try again.',
    'auth/cancelled-popup-request': 'Sign-in cancelled. Please try again.',
    'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
    'auth/internal-error': 'Internal error occurred. Please try again.',
    'auth/invalid-api-key': 'Configuration error. Please contact support.',
    'auth/app-not-authorized': 'App not authorized. Please contact support.',
    'auth/user-cancelled': 'Sign-in cancelled by user.',
    'auth/unauthorized-domain': 'This domain is not authorized for Google sign-in.',
    
    // Password reset errors
    'auth/invalid-action-code': 'This password reset link has expired or is invalid. Please request a new password reset.',
    'auth/expired-action-code': 'This password reset link has expired. Please request a new password reset.',
    'auth/user-not-found': 'No account found with this email address.',
  };

  // Clean up the error code - remove any prefixes or extra formatting
  const cleanErrorCode = errorCode?.toString().replace(/^(auth\/|error\/|firebase\/)/, '').trim();
  
  return errorMessages[cleanErrorCode] || errorMessages[errorCode] || fallbackMessage;
};

/**
 * Extracts error message from various error response formats
 * @param {Error|Object} error - The error object from API response
 * @returns {string} User-friendly error message
 */
export const extractErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred. Please try again.';
  
  // If it's already a string, try to translate it
  if (typeof error === 'string') {
    return translateAuthError(error);
  }
  
  // Handle axios error responses
  if (error.response?.data) {
    const errorData = error.response.data;
    
    // Check for detail field (FastAPI format)
    if (errorData.detail) {
      // Handle new structured error format: { message: "...", error_type: "..." }
      if (typeof errorData.detail === 'object' && errorData.detail.message) {
        return errorData.detail.message;
      }
      return translateAuthError(errorData.detail, errorData.detail);
    }
    
    // Check for error field
    if (errorData.error) {
      if (typeof errorData.error === 'string') {
        return translateAuthError(errorData.error, errorData.error);
      }
      if (errorData.error.message) {
        return translateAuthError(errorData.error.message, errorData.error.message);
      }
    }
    
    // Check for message field
    if (errorData.message) {
      return translateAuthError(errorData.message, errorData.message);
    }
  }
  
  // Handle Firebase error objects
  if (error.code) {
    return translateAuthError(error.code, error.message);
  }
  
  // Handle network errors
  if (error.message) {
    if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    if (error.message.includes('Request failed with status code')) {
      const statusMatch = error.message.match(/status code (\d+)/);
      if (statusMatch) {
        const statusCode = parseInt(statusMatch[1]);
        if (statusCode === 400) {
          return 'Invalid request. Please check your input and try again.';
        }
        if (statusCode === 401) {
          return 'Authentication failed. Please check your credentials and try again.';
        }
        if (statusCode === 403) {
          return 'Access denied. Please check your permissions.';
        }
        if (statusCode === 404) {
          return 'Service not found. Please try again later.';
        }
        if (statusCode >= 500) {
          return 'Server error. Please try again later.';
        }
      }
      return 'Request failed. Please try again.';
    }
    
    return translateAuthError(error.message, error.message);
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Determines if an error is recoverable (user can retry)
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if user should be able to retry
 */
export const isRecoverableError = (error) => {
  if (!error) return true;
  
  const nonRecoverableErrors = [
    'EMAIL_EXISTS',
    'USER_DISABLED',
    'OPERATION_NOT_ALLOWED',
    'auth/app-not-authorized',
    'auth/invalid-api-key',
    'auth/unauthorized-domain',
  ];
  
  const errorCode = error.code || error.response?.data?.detail || error.response?.data?.error || error.message || '';
  
  return !nonRecoverableErrors.some(code => errorCode.includes(code));
};

/**
 * Determines if an error suggests the user should try signing up instead
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if user should try signing up
 */
export const shouldSuggestSignUp = (error) => {
  if (!error) return false;
  
  // Check for structured error format from backend: { message: "...", error_type: "..." }
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const errorType = detail.error_type;
    // These error types suggest user should sign up
    if (['invalid_credentials', 'email_not_found'].includes(errorType)) {
      return true;
    }
  }
  
  const signUpSuggestionErrors = [
    'EMAIL_NOT_FOUND',
    'auth/user-not-found',
    'USER_NOT_FOUND',
    'INVALID_LOGIN_CREDENTIALS',  // Firebase returns this for non-existent emails too
    'no account found',  // Match user-friendly messages
    'check your email or sign up',
    "don't have an account",
  ];
  
  const errorCode = error.code || (typeof detail === 'string' ? detail : '') || error.response?.data?.error || error.message || '';
  const lowerErrorCode = errorCode.toLowerCase();
  
  return signUpSuggestionErrors.some(code => lowerErrorCode.includes(code.toLowerCase()));
};

/**
 * Determines if an error suggests the user should try signing in instead
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if user should try signing in
 */
export const shouldSuggestSignIn = (error) => {
  if (!error) return false;
  
  // Check for structured error format from backend: { message: "...", error_type: "..." }
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const errorType = detail.error_type;
    // These error types suggest user should sign in instead
    if (['email_exists'].includes(errorType)) {
      return true;
    }
  }
  
  const signInSuggestionErrors = [
    'EMAIL_EXISTS',
    'EMAIL_ALREADY_EXISTS',
    'already exists',  // Match user-friendly message from backend
    'account with this email already exists',
  ];
  
  const errorCode = error.code || (typeof detail === 'string' ? detail : '') || error.response?.data?.error || error.message || '';
  const lowerErrorCode = errorCode.toLowerCase();
  
  return signInSuggestionErrors.some(code => lowerErrorCode.includes(code.toLowerCase()));
}; 